import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import { RevealLines, Reveal, Marquee, Faq, ContactLinks, LogoMark, Steps, Audiences, SoftLanding, Partners } from "../components/shared";
import { HERO, ABOUT, STEVEN, APPROACH, SERVICES, HOURS, FORMATS, CONTACT, ACKNOWLEDGEMENT, TAGLINE } from "../content";

const Tag = ({ children }) => <p className="font-manrope text-[11px] uppercase tracking-[0.3em] text-[#56654F]">{children}</p>;

const Nav = () => (
  <header className="sticky top-0 z-40 bg-[#F6F2E9]/80 backdrop-blur-xl border-b border-[#3F4A3C]/10">
    <div className="max-w-[1440px] mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
      <a href="#top" data-testid="nav-logo" className="flex items-center gap-3"><LogoMark className="w-9 h-9" /><span className="font-outfit font-medium text-[#3F4A3C] tracking-tight">Inner You Development</span></a>
      <nav className="hidden md:flex gap-8 font-manrope text-sm text-[#4A4F45]">
        {["Approach", "Services", "Hours", "FAQ"].map((l) => <a key={l} href={`#${l.toLowerCase()}`} data-testid={`nav-${l.toLowerCase()}`} className="hover:text-[#56654F] transition-colors">{l}</a>)}
        <Link to="/team" data-testid="nav-team" className="hover:text-[#56654F] transition-colors">Team</Link>
      </nav>
      <a href={CONTACT.phoneHref} data-testid="nav-call-btn" className="font-manrope text-sm bg-[#3F4A3C] text-[#F6F2E9] px-4 py-2 hover:bg-[#56654F] transition-colors">Book now</a>
    </div>
  </header>
);

const TiltCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div className="[perspective:1200px] h-full" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative aspect-[4/5] lg:aspect-auto lg:h-full lg:min-h-[560px] [transform-style:preserve-3d]" data-testid="hero-image">
        <div className="absolute inset-0 overflow-hidden"><img src="/images/leaf-stem-corner.jpg" alt="Sage leaf stem on cream" className="w-full h-full object-cover" /></div>
        <div className="absolute -bottom-6 -left-6 sm:left-auto sm:-right-6 bg-[#F6F2E9] border border-[#3F4A3C]/10 p-5 w-56 [transform:translateZ(60px)] shadow-xl">
          <p className="font-outfit text-4xl font-light text-[#3F4A3C]">{STEVEN.years}</p>
          <p className="mt-1 font-manrope text-xs text-[#4A4F45]">years guiding individuals, couples & families</p>
        </div>
      </motion.div>
    </div>
  );
};

const Hero = () => (
  <section id="top" className="relative border-b border-[#3F4A3C]/10" data-testid="hero">
    <div className="max-w-[1440px] mx-auto grid lg:grid-cols-12 lg:min-h-[calc(100vh-4rem)]">
      <div className="lg:col-span-7 px-6 sm:px-10 py-16 lg:py-24 flex flex-col justify-between lg:border-r border-[#3F4A3C]/10">
        <Tag>{TAGLINE}</Tag>
        <RevealLines lines={HERO.lines} className="my-12 font-outfit font-light text-[#3F4A3C] text-5xl sm:text-7xl lg:text-[6.4rem] leading-[0.98] tracking-[-0.03em]" emIndex={1} emClass="text-[#5F7C8A]" />
        <Reveal delay={0.6} className="grid sm:grid-cols-2 gap-8 items-end">
          <p className="font-manrope text-base text-[#4A4F45] leading-relaxed">{HERO.sub}</p>
          <a href={CONTACT.phoneHref} data-testid="hero-book-btn" className="group flex items-center justify-between bg-[#3F4A3C] text-[#F6F2E9] px-6 py-5 font-manrope text-sm hover:bg-[#56654F] transition-colors">
            Call {CONTACT.phone} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
        </Reveal>
      </div>
      <div className="lg:col-span-5 p-6 sm:p-10 pb-14"><TiltCard /></div>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-[#3F4A3C]/10 max-w-[1440px] mx-auto">
      {FORMATS.map((f, i) => (
        <div key={f} className={`px-6 sm:px-10 py-6 border-[#3F4A3C]/10 ${i % 2 ? "" : "border-r"} lg:border-r ${i < 2 ? "border-b lg:border-b-0" : ""}`}>
          <p className="font-manrope text-[11px] text-[#5E6B57]">0{i + 1}</p><p className="mt-1 font-outfit text-lg text-[#3F4A3C]">{f}</p>
        </div>
      ))}
    </div>
  </section>
);

const Approach = () => (
  <section id="approach" className="max-w-[1440px] mx-auto px-6 sm:px-10 py-28 sm:py-36" data-testid="about-section">
    <div className="grid lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-4"><Tag>About & approach</Tag></Reveal>
      <div className="lg:col-span-8">
        <Reveal><h2 className="font-outfit font-light text-3xl sm:text-5xl leading-tight tracking-tight text-[#3F4A3C]">{ABOUT.title}</h2></Reveal>
        <Reveal delay={0.1}><p className="mt-8 font-manrope text-base sm:text-lg leading-relaxed text-[#4A4F45] max-w-2xl">{ABOUT.body[0]}</p></Reveal>
      </div>
    </div>
    <div className="mt-20 grid md:grid-cols-3 border-t border-l border-[#3F4A3C]/10">
      {APPROACH.map((a, i) => (
        <Reveal key={a.n} delay={i * 0.1} className="border-r border-b border-[#3F4A3C]/10">
          <div className="group p-8 sm:p-10 h-full hover:bg-[#E3E9E4] transition-colors duration-500" data-testid={`approach-${a.title.toLowerCase()}`}>
            <p className="font-manrope text-xs text-[#56654F]">{a.n}</p>
            <h3 className="mt-16 font-outfit text-3xl text-[#3F4A3C]">{a.title}</h3>
            <p className="mt-4 font-manrope text-sm text-[#4A4F45] leading-relaxed">{a.text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Services = () => {
  const [active, setActive] = useState(0);
  return (
    <section id="services" className="bg-[#E3E9E4] py-28 sm:py-36" data-testid="services-section">
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Tag>Services</Tag>
          <h2 className="mt-6 font-outfit font-light text-4xl sm:text-5xl text-[#3F4A3C] tracking-tight">Multidisciplinary care, under one roof.</h2>
          <div className="mt-10 relative aspect-[16/10] lg:aspect-[4/5] overflow-hidden lg:sticky lg:top-24">
            {SERVICES.map((s, i) => (
              <motion.img key={s.title} src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-contain bg-[#F6F2E9]" animate={{ opacity: active === i ? 1 : 0, scale: active === i ? 1 : 1.06 }} transition={{ duration: 0.8 }} />
            ))}
          </div>
        </div>
        <div className="lg:col-span-7 border-t border-[#3F4A3C]/15">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06}>
              <a href={CONTACT.phoneHref} onMouseEnter={() => setActive(i)} data-testid={`service-card-${i}`} className="group grid grid-cols-12 gap-4 items-center py-8 sm:py-10 border-b border-[#3F4A3C]/15">
                <span className="col-span-2 font-manrope text-xs text-[#56654F]">{String(i + 1).padStart(2, "0")}</span>
                <div className="col-span-8">
                  <h3 className="font-outfit text-2xl sm:text-3xl text-[#3F4A3C] tracking-tight group-hover:translate-x-2 transition-transform duration-500">{s.title}</h3>
                  <p className="mt-2 font-manrope text-sm text-[#4A4F45]">{s.text}</p>
                </div>
                <span className="col-span-2 justify-self-end w-11 h-11 border border-[#3F4A3C]/20 grid place-items-center group-hover:bg-[#3F4A3C] group-hover:text-white transition-colors"><Plus className="w-4 h-4 group-hover:rotate-90 transition-transform duration-500" /></span>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Hours = () => (
  <section id="hours" className="max-w-[1440px] mx-auto px-6 sm:px-10 py-28 sm:py-36 grid lg:grid-cols-12 gap-12" data-testid="visit-section">
    <Reveal className="lg:col-span-5">
      <Tag>Led by</Tag>
      <h2 className="mt-6 font-outfit font-light text-4xl sm:text-5xl text-[#3F4A3C] tracking-tight">{STEVEN.name}</h2>
      <p className="mt-2 font-manrope text-sm text-[#56654F]">{STEVEN.role}</p>
      <p className="mt-8 font-manrope text-base leading-relaxed text-[#4A4F45]">{STEVEN.body}</p>
    </Reveal>
    <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.1}>
      <Tag>Hours</Tag>
      <ul className="mt-6 border-t border-[#3F4A3C]/15" data-testid="hours-list">
        {HOURS.map((h) => <li key={h.day} className="flex justify-between gap-4 py-5 border-b border-[#3F4A3C]/15 font-manrope text-sm sm:text-base"><span>{h.day}</span><span className="text-[#3F4A3C] font-medium text-right">{h.time}</span></li>)}
      </ul>
      <p className="mt-6 font-manrope text-sm text-[#4A4F45]">{CONTACT.address}</p>
    </Reveal>
  </section>
);

const Referrals = () => (
  <section id="referrals" className="bg-[#DCE6EC]" data-testid="referrals-section">
    <div className="max-w-[1440px] mx-auto px-6 sm:px-10 py-28 sm:py-32">
      <div className="grid lg:grid-cols-12 gap-8">
        <Reveal className="lg:col-span-4"><Tag>Making a referral</Tag></Reveal>
        <Reveal className="lg:col-span-8" delay={0.1}><h2 className="font-outfit font-light text-3xl sm:text-5xl text-[#3F4A3C] tracking-tight">Four simple steps. We guide you through each one.</h2></Reveal>
      </div>
      <Steps className="mt-16" circleClass="bg-[#F6F2E9] text-[#3F4A3C]" numClass="bg-[#3F4A3C] text-[#F6F2E9] font-manrope" lineClass="bg-[#3F4A3C]/20" titleClass="font-outfit text-xl text-[#3F4A3C]" textClass="font-manrope text-sm text-[#4A4F45] leading-relaxed" />
      <div className="mt-20 pt-12 border-t border-[#3F4A3C]/15"><Tag>Who we support</Tag><Audiences className="mt-10" circleClass="bg-[#F6F2E9] text-[#3F4A3C]" labelClass="font-manrope text-sm text-[#4A4F45]" /></div>
    </div>
  </section>
);

const FaqBlock = () => (
  <section id="faq" className="border-t border-[#3F4A3C]/10" data-testid="faq-section">
    <div className="max-w-[1440px] mx-auto px-6 sm:px-10 py-28 sm:py-36 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4"><div className="lg:sticky lg:top-28"><Tag>FAQ</Tag><h2 className="mt-6 font-outfit font-light text-4xl sm:text-5xl text-[#3F4A3C] tracking-tight">Medicare, fees & first sessions.</h2></div></div>
      <Reveal className="lg:col-span-8"><Faq itemClass="border-[#3F4A3C]/15" triggerClass="font-outfit text-lg sm:text-xl text-[#3F4A3C] py-6" contentClass="font-manrope text-base text-[#4A4F45] leading-relaxed" /></Reveal>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-[#3F4A3C] text-[#F6F2E9] pb-28" data-testid="footer">
    <div className="max-w-[1440px] mx-auto px-6 sm:px-10 pt-24 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-7"><h2 className="font-outfit font-light text-4xl sm:text-6xl tracking-tight">Ready when you are.</h2>
        <ContactLinks prefix="footer" className="mt-10 flex flex-col gap-4 font-manrope text-lg" linkClass="hover:text-[#C9DAE3] transition-colors" />
      </div>
      <div className="lg:col-span-5"><p className="font-manrope text-[11px] uppercase tracking-[0.3em] text-[#C9DAE3]">Acknowledgement of Country</p><p className="mt-4 font-manrope text-sm leading-relaxed text-[#EEEADF]" data-testid="acknowledgement">{ACKNOWLEDGEMENT}</p></div>
    </div>
    <p className="mt-24 px-6 sm:px-10 font-outfit font-light text-[16vw] leading-[0.8] tracking-[-0.05em] text-[#4B5747] select-none" aria-hidden>inner you</p>
  </footer>
);

export default function Grove() {
  return (
    <main className="bg-[#F6F2E9] text-[#4A4F45] overflow-x-hidden" data-testid="grove-page">
      <Nav />
      <Hero />
      <Approach />
      <SoftLanding serif="font-outfit font-light tracking-tight" sans="font-manrope" className="border-t border-[#3F4A3C]/10" />
      <Marquee className="py-8 bg-[#3F4A3C]" itemClass="font-outfit uppercase tracking-[0.1em] text-3xl sm:text-5xl font-light text-[#EEEADF]" dotClass="text-[#E8C27A] text-base" />
      <Services />
      <Referrals />
      <Partners serif="font-outfit font-light tracking-tight" sans="font-manrope" />
      <Hours />
      <FaqBlock />
      <Footer />
    </main>
  );
}
